import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  SUPPORTED_LOCALES,
  visibleLocales,
  setLocale,
  currentLocale,
  type SupportedLocale,
} from "@/i18n";

type Variant = "header" | "card";

export function LanguageSelector({ variant = "header" }: { variant?: Variant }) {
  const { i18n, t } = useTranslation();
  // Subscribe to language changes via useTranslation's i18n state, but
  // resolve through currentLocale() so an unknown/corrupt code never
  // produces a `<select value>` that doesn't match any option.
  void i18n.resolvedLanguage;
  const current: SupportedLocale = currentLocale();
  // Preview-locale gating. Any locale flagged `preview: true` in
  // SUPPORTED_LOCALES ships in the resources bundle (so existing saved
  // sessions still resolve) but is hidden from the visible dropdown
  // until a native-speaker review pass clears it. Set
  // VITE_JINGLE_SHOW_PREVIEW_LOCALES=<code> to opt back in for a
  // staging review build. pa-Guru graduated from preview after Task
  // #272 and is now visible alongside en / es / fr-CA — the marketing
  // site, in-app picker, and SEO topic pages all surface ਪੰਜਾਬੀ.
  //
  // If the user already has a hidden preview locale active (e.g. a
  // staging build wrote one to localStorage), we MUST include their
  // current locale in the rendered options — otherwise <select value>
  // would not match any <option>, producing a blank selector and
  // React's "value not in options" warning. Hidden-current-locale
  // users can keep seeing themselves and can switch at any time.
  const baseOptions = visibleLocales();
  const options = baseOptions.some((l) => l.code === current)
    ? baseOptions
    : [
        ...baseOptions,
        SUPPORTED_LOCALES.find((l) => l.code === current) ?? baseOptions[0],
      ];

  if (variant === "card") {
    return (
      <div className="space-y-2">
        <label
          htmlFor="lang-select"
          className="text-xs uppercase tracking-widest text-amber-200/70"
        >
          {t("languageSelector.label")}
        </label>
        <select
          id="lang-select"
          value={current}
          onChange={(e) => setLocale(e.target.value as SupportedLocale)}
          data-testid="select-language"
          className="block w-full rounded-md border border-amber-200/15 bg-black/30 px-3 py-2 text-sm text-amber-100"
        >
          {options.map((l) => (
            <option key={l.code} value={l.code} className="bg-[hsl(163,50%,10%)]">
              {l.nativeName}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <label
      className="relative inline-flex items-center"
      title={t("languageSelector.label")}
    >
      <Globe className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-amber-200/80" />
      <select
        value={current}
        onChange={(e) => setLocale(e.target.value as SupportedLocale)}
        data-testid="select-language-header"
        aria-label={t("languageSelector.label")}
        className="appearance-none rounded-full border border-amber-200/25 bg-black/25 py-1 pl-7 pr-3 text-[10px] uppercase tracking-[0.18em] text-amber-100/85 transition hover:border-amber-200/55 focus:outline-none focus:ring-1 focus:ring-amber-200/40"
      >
        {options.map((l) => (
          <option key={l.code} value={l.code} className="bg-[hsl(163,50%,10%)]">
            {l.nativeName}
          </option>
        ))}
      </select>
    </label>
  );
}
